import { lazy } from 'react';

/**
 * A page left open across a deploy still knows the OLD chunk file names. The
 * next page it opens asks for a file that no longer exists; the host answers
 * with index.html, and the import fails. That is not a bug in the page, it is
 * a new version waiting, so the answer is one reload onto the new build.
 *
 * The reload is guarded: if the app already reloaded for this reason in the
 * last minute, the failure is real (or the network is down) and it is left to
 * surface as an error instead of reloading in a loop.
 */

const RELOAD_KEY = 'chunk-reload-at';
const LOOP_WINDOW_MS = 60 * 1000;

const CHUNK_ERROR = new RegExp([
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
  'is not a valid JavaScript MIME type',
  'ChunkLoadError',
].join('|'), 'i');

export function isChunkLoadError(error) {
  const text = `${error?.name || ''} ${error?.message || error || ''}`;
  return CHUNK_ERROR.test(text);
}

function readStamp() {
  try { return Number(sessionStorage.getItem(RELOAD_KEY)) || 0; } catch { return 0; }
}

/**
 * Reload once onto the new build. Returns true when a reload was started, false
 * when one already happened within the loop window (the caller should then let
 * the error show).
 */
export function reloadForNewVersion({ now = Date.now(), reload = () => window.location.reload() } = {}) {
  if (now - readStamp() < LOOP_WINDOW_MS) return false;
  try { sessionStorage.setItem(RELOAD_KEY, String(now)); } catch { /* private mode: still reload once */ }
  reload();
  return true;
}

/**
 * React.lazy for a route-level page. A stale chunk reloads the app instead of
 * reaching the error boundary; while the reload happens the Suspense fallback
 * stays up, because the returned promise never settles.
 */
export function lazyPage(factory) {
  return lazy(() => factory().catch((error) => {
    if (isChunkLoadError(error) && reloadForNewVersion()) return new Promise(() => {});
    throw error;
  }));
}

/**
 * Vite fires `vite:preloadError` when a chunk's CSS or JS dependency fails to
 * preload. Preventing the default stops Vite rethrowing it, so only do that
 * when a reload is actually on its way.
 */
export function installPreloadErrorReload(target = window) {
  target.addEventListener('vite:preloadError', (event) => {
    if (reloadForNewVersion()) event.preventDefault();
  });
}
